import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react"
import { useRouter } from "next/router"

import { useScopedStorageKey } from "@/workspaces/scope"

import type { Persona } from "@/types/api/Persona"

import { useCopyPersonaCatalogEntryTransport } from "./usePersonaCatalogTransport"
import { mapTransportPersonaToPersona } from "./usePersonasTransport"

interface PersonaCatalogSelectionOptions {
  enabled: boolean
  pickerOpen: boolean
  singleSelect: boolean
  setDraftIds: Dispatch<SetStateAction<string[]>>
  cancelPicker: () => void
}

export function usePersonaCatalogSelectionTransport({
  enabled,
  pickerOpen,
  singleSelect,
  setDraftIds,
  cancelPicker,
}: PersonaCatalogSelectionOptions) {
  const router = useRouter()
  const scopeKey = useScopedStorageKey("persona-catalog-selection")
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [copies, setCopies] = useState<{
    scopeKey: string | undefined
    personas: Persona[]
  }>({ scopeKey, personas: [] })
  const generationRef = useRef(0)
  const previousScopeRef = useRef(scopeKey)
  const copyQueueRef = useRef<Promise<void>>(Promise.resolve())
  const { copyPersonaCatalogEntry } = useCopyPersonaCatalogEntryTransport()

  const closeCatalog = useCallback(() => {
    generationRef.current += 1
    copyQueueRef.current = Promise.resolve()
    setCatalogOpen(false)
  }, [])

  useEffect(() => {
    closeCatalog()
    const scopeChanged =
      previousScopeRef.current !== undefined &&
      previousScopeRef.current !== scopeKey
    previousScopeRef.current = scopeKey
    if (enabled && scopeChanged) {
      cancelPicker()
    }
    const invalidatePicker = () => {
      closeCatalog()
      cancelPicker()
    }
    router.events.on("routeChangeStart", invalidatePicker)
    return () => {
      router.events.off("routeChangeStart", invalidatePicker)
      generationRef.current += 1
    }
  }, [
    cancelPicker,
    closeCatalog,
    enabled,
    pickerOpen,
    router.events,
    scopeKey,
    singleSelect,
  ])

  const openCatalog = useCallback(() => {
    if (enabled && pickerOpen && scopeKey) {
      setCatalogOpen(true)
    }
  }, [enabled, pickerOpen, scopeKey])

  const addCatalogPersona = useCallback(
    (entryId: string) => {
      const generation = generationRef.current
      const copyAndSelect = async () => {
        if (generation !== generationRef.current) {
          return
        }
        const persona = await copyPersonaCatalogEntry(entryId)
        if (generation !== generationRef.current) {
          return
        }
        const copy = mapTransportPersonaToPersona(persona)
        setCopies((current) => ({
          scopeKey,
          personas: [
            ...(current.scopeKey === scopeKey ? current.personas : []),
            copy,
          ],
        }))
        setDraftIds((ids) =>
          singleSelect ? [copy.id] : [...new Set([...ids, copy.id])],
        )
      }
      if (!singleSelect) {
        return copyAndSelect()
      }
      const queuedCopy = copyQueueRef.current.then(copyAndSelect)
      copyQueueRef.current = queuedCopy.then(
        () => undefined,
        () => undefined,
      )
      return queuedCopy
    },
    [copyPersonaCatalogEntry, scopeKey, setDraftIds, singleSelect],
  )

  return {
    catalogOpen: enabled && pickerOpen && catalogOpen,
    copiedPersonas: copies.scopeKey === scopeKey ? copies.personas : [],
    canOpenCatalog: enabled && pickerOpen && Boolean(scopeKey),
    openCatalog,
    closeCatalog,
    addCatalogPersona,
  }
}
